import React from 'react';

// 🎯 EVENT-DRIVEN MODAL PERSISTENCE SYSTEM
// ========================================
// Save only when meaningful user actions occur - no more infinite loops or console spam!
//
// EXAMPLE USAGE:
// ```tsx
// import { useEventDrivenModalPersistence } from '../utils/modalPersistence';
//
// function AnyModal({ isOpen, onClose }) {
//   const [name, setName] = useState('');
//   const [email, setEmail] = useState('');
//   
//   // ✨ Get persistence functions
//   const { loadModalData, saveModalData, clearModalData } = useEventDrivenModalPersistence('my_modal');
//
//   // Load data when modal opens
//   useEffect(() => {
//     if (isOpen) {
//       const saved = loadModalData();
//       if (saved.name) setName(saved.name);
//       if (saved.email) setEmail(saved.email);
//     }
//   }, [isOpen]);
//
//   // Save on specific user actions
//   const handleNameChange = (newName) => {
//     setName(newName);
//     saveModalData({ name: newName, email }); // Explicit save
//   };
//
//   const handleEmailChange = (newEmail) => {
//     setEmail(newEmail);
//     saveModalData({ name, email: newEmail }); // Explicit save
//   };
//
//   // Clear when done
//   const handleSubmit = () => {
//     // ... create record
//     clearModalData(); // Clear after success
//     onClose();
//   };
// }
// ```

interface EventDrivenModalState {
  formData: Record<string, any>;
  timestamp: number;
}

const EXPIRATION_HOURS = 24; // 24 hours expiration

class EventDrivenModalPersistence {
  private static instance: EventDrivenModalPersistence;

  static getInstance(): EventDrivenModalPersistence {
    if (!EventDrivenModalPersistence.instance) {
      EventDrivenModalPersistence.instance = new EventDrivenModalPersistence();
    }
    return EventDrivenModalPersistence.instance;
  }

  // Save modal data (only when explicitly called)
  saveModalData(modalId: string, formData: Record<string, any>): void {
    try {
      const modalState: EventDrivenModalState = {
        formData,
        timestamp: Date.now()
      };
      
      const storageKey = `gitbooked_modal_${modalId}`;
      localStorage.setItem(storageKey, JSON.stringify(modalState));
      console.log(`💾 Saved ${modalId} progress`);
    } catch (error) {
      console.error(`💾 Failed to save ${modalId} progress:`, error);
    }
  }

  // Load modal data
  loadModalData(modalId: string): Record<string, any> {
    try {
      const storageKey = `gitbooked_modal_${modalId}`;
      const stored = localStorage.getItem(storageKey);
      
      if (!stored) return {};

      const modalState: EventDrivenModalState = JSON.parse(stored);
      
      // Check if expired
      const now = Date.now();
      const ageHours = (now - modalState.timestamp) / (1000 * 60 * 60);
      
      if (ageHours > EXPIRATION_HOURS) {
        console.log(`🗑️ ${modalId} data expired, clearing localStorage`);
        this.clearModalData(modalId);
        return {};
      }
      
      console.log(`📥 Loaded ${modalId} progress`);
      return modalState.formData;
    } catch (error) {
      console.error(`📥 Failed to load ${modalId} progress:`, error);
      return {};
    }
  }

  // Clear modal data
  clearModalData(modalId: string): void {
    try {
      const storageKey = `gitbooked_modal_${modalId}`;
      localStorage.removeItem(storageKey);
      console.log(`🗑️ Cleared ${modalId} progress`);
    } catch (error) {
      console.error(`🗑️ Failed to clear ${modalId} progress:`, error);
    }
  }

  // Check if modal has saved data
  hasModalData(modalId: string): boolean {
    const data = this.loadModalData(modalId);
    return Object.keys(data).length > 0;
  }
}

// Export singleton instance
export const eventDrivenModalPersistence = EventDrivenModalPersistence.getInstance();

// Event-Driven React Hook for ANY modal
export function useEventDrivenModalPersistence(modalId: string) {
  const saveModalData = React.useCallback((formData: Record<string, any>) => {
    eventDrivenModalPersistence.saveModalData(modalId, formData);
  }, [modalId]);

  const loadModalData = React.useCallback(() => {
    return eventDrivenModalPersistence.loadModalData(modalId);
  }, [modalId]);

  const clearModalData = React.useCallback(() => {
    eventDrivenModalPersistence.clearModalData(modalId);
  }, [modalId]);

  const hasModalData = React.useCallback(() => {
    return eventDrivenModalPersistence.hasModalData(modalId);
  }, [modalId]);

  return { saveModalData, loadModalData, clearModalData, hasModalData };
}

// 🎯 GENERIC MODAL PERSISTENCE SYSTEM (LEGACY - CAUSES INFINITE LOOPS)
// =====================================================================
// This is the old reactive system that causes infinite loops and console spam.
// It's kept for backward compatibility but should be replaced with the event-driven system above.

interface GenericModalState {
  isOpen: boolean;
  formData: Record<string, any>;
  timestamp: number;
}

const EXPIRATION_MINUTES = 5;

class GenericModalPersistence {
  private static instance: GenericModalPersistence;

  static getInstance(): GenericModalPersistence {
    if (!GenericModalPersistence.instance) {
      GenericModalPersistence.instance = new GenericModalPersistence();
    }
    return GenericModalPersistence.instance;
  }

  // Save any modal state with a unique ID
  saveModalState(modalId: string, isOpen: boolean, formData: Record<string, any>): void {
    try {
      const modalState: GenericModalState = {
        isOpen,
        formData,
        timestamp: Date.now()
      };
      
      const storageKey = `gitbooked_modal_${modalId}`;
      console.log(`💾 SAVING ${modalId} modal:`, formData);
      
      localStorage.setItem(storageKey, JSON.stringify(modalState));
      console.log(`💾 ${modalId} SAVED SUCCESSFULLY`);
    } catch (error) {
      console.error(`💾 SAVE FAILED for ${modalId}:`, error);
    }
  }

  // Get any modal state by ID
  getModalState(modalId: string): GenericModalState | null {
    try {
      const storageKey = `gitbooked_modal_${modalId}`;
      const stored = localStorage.getItem(storageKey);
      
      if (!stored) return null;

      const modalState: GenericModalState = JSON.parse(stored);
      
      // Check if expired
      const now = Date.now();
      const ageMinutes = (now - modalState.timestamp) / (1000 * 60);
      
      if (ageMinutes > EXPIRATION_MINUTES) {
        console.log(`🔄 ${modalId} state expired, clearing localStorage`);
        this.clearModalState(modalId);
        return null;
      }
      
      console.log(`🔄 ${modalId} state restored:`, modalState.formData);
      return modalState;
    } catch (error) {
      console.error(`🔄 RESTORATION FAILED for ${modalId}:`, error);
      return null;
    }
  }

  // Clear any modal state by ID
  clearModalState(modalId: string): void {
    try {
      const storageKey = `gitbooked_modal_${modalId}`;
      localStorage.removeItem(storageKey);
      console.log(`🗑️ ${modalId} state cleared`);
    } catch (error) {
      console.error(`🗑️ CLEAR FAILED for ${modalId}:`, error);
    }
  }

  // Check if modal has saved state
  hasModalState(modalId: string): boolean {
    return this.getModalState(modalId) !== null;
  }
}

// Export singleton instance
export const genericModalPersistence = GenericModalPersistence.getInstance();

// Generic React Hook for ANY modal
export function useGenericModalPersistence(modalId: string) {
  return {
    saveModal: (isOpen: boolean, formData: Record<string, any>) => 
      genericModalPersistence.saveModalState(modalId, isOpen, formData),
    getModal: () => genericModalPersistence.getModalState(modalId),
    clearModal: () => genericModalPersistence.clearModalState(modalId),
    hasModal: () => genericModalPersistence.hasModalState(modalId)
  };
}

// ⚠️ DEPRECATED: Auto-persistence hook (CAUSES INFINITE LOOPS)
// This hook causes infinite loops and console spam - use useEventDrivenModalPersistence instead
export function useAutoModalPersistence(modalId: string, isOpen: boolean, formData: Record<string, any>) {
  const { saveModal, getModal, clearModal } = useGenericModalPersistence(modalId);
  
  // ⚠️ THIS CAUSES INFINITE LOOPS - formData changes every render!
  React.useEffect(() => {
    if (isOpen && Object.keys(formData).some(key => formData[key] !== undefined && formData[key] !== null && formData[key] !== '')) {
      console.log(`💾 Auto-saving ${modalId}...`);
      saveModal(isOpen, formData);
    }
  }, [isOpen, formData, modalId, saveModal]);

  // Return restoration function
  const restoreModal = React.useCallback(() => {
    const saved = getModal();
    return saved?.formData || {};
  }, [getModal]);

  return { restoreModal, clearModal };
}

// Legacy interfaces (keeping for backward compatibility)
interface BookingModalState {
  isOpen: boolean;
  bookingId?: string;
  bookingData?: any;
  activeTab?: string;
  timestamp: number;
}

interface CreateBookingModalState {
  isOpen: boolean;
  formData?: {
    title?: string;
    isLocationBooking?: boolean;
    selectedCustomer?: any;
    selectedServices?: any[];
    scheduleData?: {
      dropOffDate?: string;
      dropOffTime?: string;
      startDate?: string;
      startTime?: string;
      endDate?: string;
      endTime?: string;
      customer_notes?: string;
    };
    bookingColor?: string;
    bookingSecondaryColor?: string | null;
    selectedServiceAddons?: Record<string, any[]>;
  };
  timestamp: number;
}

// Legacy implementation (keeping for BookingInfoModal)
const BOOKING_MODAL_STORAGE_KEY = 'gitbooked_booking_modal_state';
const CREATE_BOOKING_MODAL_STORAGE_KEY = 'gitbooked_create_booking_modal_state';

class ModalPersistence {
  private static instance: ModalPersistence;

  static getInstance(): ModalPersistence {
    if (!ModalPersistence.instance) {
      ModalPersistence.instance = new ModalPersistence();
    }
    return ModalPersistence.instance;
  }

  // Save modal state to localStorage
  saveModalState(state: Omit<BookingModalState, 'timestamp'>): void {
    try {
      const modalState: BookingModalState = {
        ...state,
        timestamp: Date.now()
      };
      
      localStorage.setItem(BOOKING_MODAL_STORAGE_KEY, JSON.stringify(modalState));
      console.log('📱 Modal state saved to localStorage:', modalState);
    } catch (error) {
      console.warn('Failed to save modal state to localStorage:', error);
    }
  }

  // Get modal state from localStorage
  getModalState(): BookingModalState | null {
    try {
      const stored = localStorage.getItem(BOOKING_MODAL_STORAGE_KEY);
      if (!stored) return null;

      const modalState: BookingModalState = JSON.parse(stored);
      
      // Check if expired (5 minutes)
      const now = Date.now();
      const ageMinutes = (now - modalState.timestamp) / (1000 * 60);
      
      if (ageMinutes > EXPIRATION_MINUTES) {
        console.log('📱 Modal state expired, clearing localStorage');
        this.clearModalState();
        return null;
      }
      
      console.log('📱 Modal state restored from localStorage:', modalState);
      return modalState;
    } catch (error) {
      console.warn('Failed to get modal state from localStorage:', error);
      return null;
    }
  }

  // Clear modal state from localStorage
  clearModalState(): void {
    try {
      localStorage.removeItem(BOOKING_MODAL_STORAGE_KEY);
      console.log('📱 Modal state cleared from localStorage');
    } catch (error) {
      console.warn('Failed to clear modal state from localStorage:', error);
    }
  }

  // Check if there's a valid saved modal state
  hasSavedModalState(): boolean {
    return this.getModalState() !== null;
  }

  // Update just the active tab without affecting other state
  updateActiveTab(activeTab: string): void {
    const currentState = this.getModalState();
    if (currentState && currentState.isOpen) {
      this.saveModalState({
        isOpen: currentState.isOpen,
        bookingId: currentState.bookingId,
        bookingData: currentState.bookingData,
        activeTab
      });
    }
  }

  // Update booking data without affecting other state
  updateBookingData(bookingData: any): void {
    const currentState = this.getModalState();
    if (currentState && currentState.isOpen) {
      this.saveModalState({
        isOpen: currentState.isOpen,
        bookingId: currentState.bookingId,
        bookingData,
        activeTab: currentState.activeTab
      });
    }
  }

  // Save CreateBookingModal state to localStorage
  saveCreateBookingModalState(state: Omit<CreateBookingModalState, 'timestamp'>): void {
    try {
      const modalState: CreateBookingModalState = {
        ...state,
        timestamp: Date.now()
      };
      
      console.log('📱 modalPersistence: Saving CreateBookingModal state:', modalState);
      console.log('📱 modalPersistence: selectedCustomer being saved:', modalState.formData?.selectedCustomer);
      
      localStorage.setItem(CREATE_BOOKING_MODAL_STORAGE_KEY, JSON.stringify(modalState));
      console.log('📱 CreateBookingModal state saved to localStorage:', modalState);
    } catch (error) {
      console.warn('Failed to save CreateBookingModal state to localStorage:', error);
    }
  }

  // Get CreateBookingModal state from localStorage
  getCreateBookingModalState(): CreateBookingModalState | null {
    try {
      const stored = localStorage.getItem(CREATE_BOOKING_MODAL_STORAGE_KEY);
      console.log('📱 modalPersistence: Raw localStorage data:', stored);
      
      if (!stored) return null;

      const modalState: CreateBookingModalState = JSON.parse(stored);
      console.log('📱 modalPersistence: Parsed CreateBookingModal state:', modalState);
      console.log('📱 modalPersistence: selectedCustomer being retrieved:', modalState.formData?.selectedCustomer);
      
      // Check if expired (5 minutes)
      const now = Date.now();
      const ageMinutes = (now - modalState.timestamp) / (1000 * 60);
      
      if (ageMinutes > EXPIRATION_MINUTES) {
        console.log('📱 CreateBookingModal state expired, clearing localStorage');
        this.clearCreateBookingModalState();
        return null;
      }
      
      console.log('📱 CreateBookingModal state restored from localStorage:', modalState);
      return modalState;
    } catch (error) {
      console.warn('Failed to get CreateBookingModal state from localStorage:', error);
      return null;
    }
  }

  // Clear CreateBookingModal state from localStorage
  clearCreateBookingModalState(): void {
    try {
      localStorage.removeItem(CREATE_BOOKING_MODAL_STORAGE_KEY);
      console.log('📱 CreateBookingModal state cleared from localStorage');
    } catch (error) {
      console.warn('Failed to clear CreateBookingModal state from localStorage:', error);
    }
  }

  // Check if there's a valid saved CreateBookingModal state
  hasSavedCreateBookingModalState(): boolean {
    return this.getCreateBookingModalState() !== null;
  }
}

// Export singleton instance
export const modalPersistence = ModalPersistence.getInstance();

// React hook for using modal persistence
export function useModalPersistence() {
  return {
    saveModalState: modalPersistence.saveModalState.bind(modalPersistence),
    getModalState: modalPersistence.getModalState.bind(modalPersistence),
    clearModalState: modalPersistence.clearModalState.bind(modalPersistence),
    hasSavedModalState: modalPersistence.hasSavedModalState.bind(modalPersistence),
    updateActiveTab: modalPersistence.updateActiveTab.bind(modalPersistence),
    updateBookingData: modalPersistence.updateBookingData.bind(modalPersistence),
    saveCreateBookingModalState: modalPersistence.saveCreateBookingModalState.bind(modalPersistence),
    getCreateBookingModalState: modalPersistence.getCreateBookingModalState.bind(modalPersistence),
    clearCreateBookingModalState: modalPersistence.clearCreateBookingModalState.bind(modalPersistence),
    hasSavedCreateBookingModalState: modalPersistence.hasSavedCreateBookingModalState.bind(modalPersistence)
  };
} 